import { useState } from 'react';
import TimedNotice from '../timedNotice.js';
import {
  countRequiredBlankFields,
  validateReviewContinue,
} from '../../domain/draftInvalidation.js';
import type {
  AnswerSource,
  AnswerStatus,
  AssumptionRecord,
  ConnectedCrowd,
  OperatingSchedule,
  RhythmAnswer,
  TimePeriod,
  WorldDraft,
  CharacterDraft,
  CharacterVisit,
} from '../../domain/types.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import {
  CONNECTED_QUESTIONS,
  crowdLabel,
  GROUNDING_FIELDS,
  isRhythmSkipped,
  rhythmLabel,
} from './questionSchema.js';
import CharacterPopulationAdapter from './CharacterPopulationAdapter.js';
import type { CharacterBodySnapshot } from './characterBody.js';
import type { CharacterBond } from '../../domain/types.js';
import RelationshipWire from './RelationshipWire.js';
import './createWorldSteps.css';
import { isHostedSite } from '../../llm/hosted.js';

export { LocationGroundingStep } from './groundingStep.js';
export { ConnectedStep, ConditionalQuestionStep } from './connectedStep.js';
export { TimePeriodStep } from './periodStep.js';
export { OperatingHoursStep, OperatingHoursStepInit } from './hoursStep.js';

function sourceLabel(source: AnswerSource | null): string {
  if (!source) return 'unknown';
  if (source === 'user') return 'you';
  return source.replace('_', ' ');
}

function statusBadge(status: AnswerStatus): JSX.Element | null {
  if (status === 'stale') return <span className="world-badge world-badge-stale">Stale</span>;
  if (status === 'suggested') return <span className="world-badge world-badge-suggested">Suggested</span>;
  return null;
}

export function formatTimePeriodSummary(period: TimePeriod | null | undefined): string {
  if (!period) return '';
  const present = period.present?.trim() ?? '';
  const era = period.period?.trim() ?? '';
  if (present && era) return `${present} · ${era}`;
  return present || era;
}

export function formatScheduleSummary(schedule: OperatingSchedule | null | undefined): {
  hours: string;
  timezone: string;
} {
  if (!schedule) {
    return { hours: 'unset', timezone: 'location' };
  }
  const timezone =
    schedule.timezoneStrategy === 'explicit' || schedule.timezoneStrategy === 'location'
      ? (schedule.timezone?.trim() || 'unset')
      : schedule.timezoneStrategy === 'device'
        ? 'device'
        : 'unset';
  if (!schedule.hasOperatingHours) {
    return { hours: 'Live 24/7', timezone };
  }
  return {
    hours: `${schedule.openTime}–${schedule.closeTime}`,
    timezone,
  };
}

function rhythmWithType(rhythm: RhythmAnswer): rhythm is Extract<RhythmAnswer, { skipped: false }> {
  return !rhythm.skipped;
}

function formatConnectedValue(questionId: string, value: unknown): string {
  if (questionId === WIZARD_QUESTION_IDS.connectedCrowd && value && typeof value === 'object') {
    const crowd = value as ConnectedCrowd;
    if (crowd.type === 'other') return crowd.otherDescription?.trim() || 'Other';
    return crowdLabel(crowd.type);
  }
  if (questionId === WIZARD_QUESTION_IDS.connectedRhythm && value && typeof value === 'object') {
    const rhythm = value as RhythmAnswer;
    if (isRhythmSkipped(rhythm)) return 'Optional — skipped';
    if (rhythmWithType(rhythm)) {
      if (rhythm.type === 'other') return rhythm.otherDescription?.trim() || 'Other';
      return rhythmLabel(rhythm.type);
    }
  }
  return String(value ?? '');
}

function connectedFieldValue(
  questionId: string,
  draft: WorldDraft,
): { text: string; source: AnswerSource | null; status: AnswerStatus | 'missing' } {
  const answer = draft.answers[questionId];
  const connected = draft.connected;
  if (answer) {
    return {
      text: formatConnectedValue(questionId, answer.value),
      source: answer.source,
      status: answer.status,
    };
  }
  if (!connected) return { text: '', source: null, status: 'missing' };
  if (questionId === WIZARD_QUESTION_IDS.connectedLocationType) {
    return { text: connected.locationType, source: 'user', status: 'answered' };
  }
  if (questionId === WIZARD_QUESTION_IDS.connectedDescription) {
    return { text: connected.description, source: 'user', status: 'answered' };
  }
  if (questionId === WIZARD_QUESTION_IDS.connectedCrowd) {
    return {
      text: formatConnectedValue(questionId, connected.crowd),
      source: 'user',
      status: 'answered',
    };
  }
  return {
    text: formatConnectedValue(questionId, connected.rhythm),
    source: 'user',
    status: isRhythmSkipped(connected.rhythm) ? 'skipped' : 'answered',
  };
}

/** Review continue gate — enforces blankCount === 0 plus acceptance and stale checks. */
export function reviewStepContinueBlock(
  draft: WorldDraft,
  confirmed: boolean,
): string | null {
  const { count } = countRequiredBlankFields(draft);
  if (count > 0) {
    return `you have ${count} blank unfilled questions`;
  }
  const check = validateReviewContinue({ ...draft, reviewAccepted: confirmed });
  if (!check.ok) {
    return check.errors[0] ?? 'Review is incomplete.';
  }
  return null;
}

export function WelcomeStep(): JSX.Element {
  const hosted = isHostedSite();
  return (
    <div className="world-copy">
      <p>Create a place, choose a time and populate it with characters. The world keeps advancing while you watch.</p>
      <ul>
        <li>History remains after you leave.</li>
        <li>Generated suggestions are always editable.</li>
        {!hosted && <li>API key required for AI suggestions during creation.</li>}
      </ul>
    </div>
  );
}

export function ReviewStep(props: {
  draft: WorldDraft;
  confirmed: boolean;
  onConfirm: () => void;
  onEditSection: (step: string) => void;
  onEditAnswer: (questionId: string) => void;
  onAcceptAnswer: (questionId: string) => void;
  onRegenerateAnswer: (questionId: string) => void;
  onRegenerateSection: (field: 'location' | 'timePeriod' | 'schedule') => void;
  onEditCharacter: (draftCharacterId: string) => void;
  onAddCharacter: () => void;
  bodies?: Record<string, CharacterBodySnapshot>;
  onBondsChange?: (bonds: CharacterBond[]) => void;
  onVisitsChange?: (characterId: string, visits: CharacterVisit[], visitor?: boolean) => void;
  onFillBlanks?: () => void;
  onDeclineFillBlanks?: () => void;
  /** @deprecated v2 assumptions block removed — ignored for CreateWorldWizard compat. */
  onAcceptAssumption?: (assumption: AssumptionRecord) => void;
  /** @deprecated v2 assumptions block removed — ignored for CreateWorldWizard compat. */
  onChangeAssumption?: (assumption: AssumptionRecord) => void;
  /** @deprecated v2 assumptions block removed — ignored for CreateWorldWizard compat. */
  onRegenerateAssumption?: (assumption: AssumptionRecord) => void;
}): JSX.Element {
  const d = props.draft;
  const { count, blanks } = countRequiredBlankFields(d);
  const schedule = formatScheduleSummary(d.schedule);
  const activeCharacters = d.characterDrafts.filter((c) => c.status !== 'removed');
  const [blankChoice, setBlankChoice] = useState<'yes' | 'no' | null>(null);

  function handleBlankYes(): void {
    setBlankChoice('yes');
    props.onFillBlanks?.();
  }

  function handleBlankNo(): void {
    setBlankChoice('no');
    props.onDeclineFillBlanks?.();
  }

  return (
    <div className="world-review">
      <p className="world-step-subtitle">Review and ai fill-ins</p>

      <section className="world-review-card">
        <h3>Location</h3>
        <ul className="world-review-list">
          {GROUNDING_FIELDS.map((field) => {
            const answer = d.answers[field.questionId];
            const value =
              field.questionId === WIZARD_QUESTION_IDS.groundingName
                ? d.location?.name
                : field.questionId === WIZARD_QUESTION_IDS.groundingRegion
                  ? d.location?.region
                  : d.location?.language;
            const status = answer?.status ?? (value?.trim() ? 'answered' : 'unanswered');
            const source = answer?.source ?? (value?.trim() ? 'user' : null);
            return (
              <li key={field.questionId} className={status === 'stale' ? 'world-stale-row' : ''}>
                <strong>{field.label}</strong>
                <div>
                  {value || '—'}{' '}
                  <span className="world-muted">({sourceLabel(source)}, {status})</span>
                  {statusBadge(status as AnswerStatus)}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="world-action-row world-review-actions">
          <button type="button" className="btn-ghost" aria-label="Edit location" onClick={() => props.onEditSection('location-grounding')}>Edit</button>
          <button type="button" className="btn-ghost" aria-label="Regenerate location" onClick={() => props.onRegenerateSection('location')}>Let AI redo/choose</button>
        </div>
      </section>

      <section className="world-review-card">
        <h3>Connected answers</h3>
        <ul className="world-review-list">
          {CONNECTED_QUESTIONS.map((q) => {
            const row = connectedFieldValue(q.questionId, d);
            if (!row.text && row.status === 'missing') return null;
            const generated = row.source && row.source !== 'user';
            const stale = row.status === 'stale';
            return (
              <li key={q.questionId} className={stale ? 'world-stale-row' : ''}>
                <div>
                  <strong>{q.prompt}</strong>
                  <div>
                    {row.text || '—'}{' '}
                    <span className="world-muted">({sourceLabel(row.source)}, {row.status})</span>
                    {statusBadge(row.status as AnswerStatus)}
                  </div>
                </div>
                <div className="world-action-row world-review-actions">
                  <button type="button" className="btn-ghost" aria-label={`Edit ${q.prompt}`} onClick={() => props.onEditAnswer(q.questionId)}>Edit</button>
                  {generated && row.status !== 'missing' && (
                    <>
                      <button type="button" className="btn-primary" onClick={() => props.onAcceptAnswer(q.questionId)}>Accept</button>
                      <button type="button" className="btn-ghost" onClick={() => props.onRegenerateAnswer(q.questionId)}>Let AI redo/choose</button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="world-review-card">
        <h3>Time period</h3>
        <p>{formatTimePeriodSummary(d.timePeriod) || '—'}</p>
        {d.timePeriod?.rationale?.trim() && (
          <p className="world-muted">{d.timePeriod.rationale}</p>
        )}
        <div className="world-action-row world-review-actions">
          <button type="button" className="btn-ghost" aria-label="Edit time period" onClick={() => props.onEditSection('time-period')}>Edit</button>
          <button type="button" className="btn-ghost" aria-label="Regenerate time period" onClick={() => props.onRegenerateSection('timePeriod')}>Let AI redo/choose</button>
        </div>
      </section>

      <section className="world-review-card">
        <h3>Schedule</h3>
        <p>{schedule.hours}</p>
        <p className="world-muted">Timezone: {schedule.timezone}</p>
        <div className="world-action-row world-review-actions">
          <button type="button" className="btn-ghost" aria-label="Edit schedule" onClick={() => props.onEditSection('operating-hours')}>Edit</button>
          <button type="button" className="btn-ghost" aria-label="Regenerate schedule" onClick={() => props.onRegenerateSection('schedule')}>Let AI redo/choose</button>
        </div>
      </section>

      <section className="world-review-card">
        <h3>Characters</h3>
        {activeCharacters.length === 0 ? (
          <>
            <p>None yet</p>
            <button type="button" className="btn-ghost" onClick={() => props.onAddCharacter()}>Add character</button>
          </>
        ) : (
          <>
            <RelationshipWire
              characters={activeCharacters}
              bodies={props.bodies ?? {}}
              bonds={d.characterBonds ?? []}
              onChange={(bonds) => props.onBondsChange?.(bonds)}
              onVisitsChange={props.onVisitsChange}
              onEditCharacter={props.onEditCharacter}
            />
            {activeCharacters.length < 10 && (
              <button type="button" className="btn-ghost" onClick={() => props.onAddCharacter()}>Add character</button>
            )}
          </>
        )}
      </section>

      <section className="world-review-blank-check" aria-live="polite">
        {count > 0 ? (
          <>
            <p className="world-review-blank-prompt">
              you have {count} blank unfilled questions, would you like AI to fill them in: yes/no
            </p>
            <ul className="world-review-blank-list">
              {blanks.map((b) => (
                <li key={b.fieldId}>{b.label}</li>
              ))}
            </ul>
            <div className="world-action-row world-review-blank-actions">
              <button type="button" className="btn-primary" onClick={handleBlankYes}>yes</button>
              <button type="button" className="btn-ghost" onClick={handleBlankNo}>no</button>
            </div>
            {blankChoice === 'no' && (
              <p className="world-muted">Required blanks still block Continue until filled or accepted.</p>
            )}
          </>
        ) : (
          <p className="world-review-blank-zero">you have 0 blank unfilled questions</p>
        )}
      </section>

      <label className="world-check">
        <input type="checkbox" checked={props.confirmed} onChange={() => props.onConfirm()} />
        I have reviewed this world and accept the assumptions shown.
      </label>
    </div>
  );
}

export function GenerationStep(props: {
  stage: string;
  error: string | null;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="world-gen">
      <p role="status">{props.stage || 'Preparing world…'}</p>
      {props.error && (
        <>
          <TimedNotice text={props.error} className="world-error" role="alert" />
          <button type="button" className="btn-primary" onClick={props.onRetry}>Use local fallback</button>
        </>
      )}
    </div>
  );
}

export function CharacterPopulationStep(props: {
  draft: WorldDraft;
  bodies: Record<string, CharacterBodySnapshot>;
  onDraftChange: (draft: WorldDraft) => void;
  onBodiesChange: (bodies: Record<string, CharacterBodySnapshot>) => void;
  onBack: () => void;
  onSaveToast: () => void;
  onReset: () => void;
  onCharacterFlowComplete?: () => void;
  onSavedToLocation?: (draft: WorldDraft, bodies: Record<string, CharacterBodySnapshot>) => void;
  onPhaseChange?: (phase: import('./CharacterPopulationAdapter.js').CharacterPhase) => void;
  startEditId?: string | null;
  onStartEditConsumed?: () => void;
  shellOwnsContinue?: boolean;
  onRegisterContinue?: (run: (() => void) | null, disabled: boolean) => void;
  onRegisterBack?: (run: (() => void) | null) => void;
  captureWorking?: { current: (() => CharacterDraft | null) | null };
}): JSX.Element {
  return (
    <CharacterPopulationAdapter
      draft={props.draft}
      bodies={props.bodies}
      onDraftChange={props.onDraftChange}
      onBodiesChange={props.onBodiesChange}
      onBack={props.onBack}
      onSaveToast={props.onSaveToast}
      onReset={props.onReset}
      onCharacterFlowComplete={props.onCharacterFlowComplete}
      onSavedToLocation={props.onSavedToLocation}
      onPhaseChange={props.onPhaseChange}
      startEditId={props.startEditId}
      onStartEditConsumed={props.onStartEditConsumed}
      shellOwnsContinue={props.shellOwnsContinue}
      onRegisterContinue={props.onRegisterContinue}
      onRegisterBack={props.onRegisterBack}
      captureWorking={props.captureWorking}
    />
  );
}
